import { QueryClient } from '@tanstack/react-query';

// The app's one QueryClient is built from these options in index.jsx. They are exported so a
// test can build a client with the same defaults: a suite that makes its own
// `new QueryClient({ defaultOptions: { queries: { retry: false } } })` runs with staleTime 0
// and no mutation retry, and so cannot see a page serving a five-minute-old copy or an
// action being sent twice.

/**
 * ⚡ PERFORMANCE OPTIMIZATION: Enhanced React Query configuration
 *
 * Key optimizations:
 * 1. Optimistic updates with rollback
 * 2. Request deduplication (structuralSharing)
 * 3. Garbage collection tuning
 * 4. Background refetching controls
 * 5. Network status awareness
 */
export const queryClientOptions = {
  defaultOptions: {
    queries: {
      // ⚡ Cache configuration
      staleTime: 5 * 60 * 1000, // Data considered fresh for 5 minutes
      gcTime: 30 * 60 * 1000,   // Garbage collect unused data after 30 minutes

      // ⚡ Refetching behavior
      refetchOnWindowFocus: false,  // Prevent aggressive refetching
      refetchOnReconnect: 'always', // Refetch when network reconnects
      refetchOnMount: true,         // Refetch when component mounts if stale

      // ⚡ Retry configuration with exponential backoff
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // ⚡ Enable structural sharing to detect actual data changes
      structuralSharing: true,

      // ⚡ Network mode for offline support
      networkMode: 'online',
    },
    mutations: {
      // ⚡ Mutation retry configuration
      retry: 1,
      retryDelay: 1000,
      networkMode: 'online',
    },
  },
};

export const createAppQueryClient = () => new QueryClient(queryClientOptions);
